import { PaginatedResource, Resource } from "@domain/base/Resource";
import { IInventoryRepository } from "@domain/inventory/IInventoryRepository";
import { ProductInventory, WarehouseInventory } from "@domain/inventory/models/Inventory";
import { InventoryMutation } from "@domain/inventory/models/InventoryMutation";
import { InventoryDataSource } from "../datasources/InventoryDataSource";
import { InventoryMapper } from "../mappers/InventoryMapper";
import { InventoryServiceLevelMapper } from "../mappers/InventoryServiceLevelMapper";
import { InventoryStockLevelsMapper } from "../mappers/InventoryStockLevelsMapper";
import { InventoryFilter } from "@domain/inventory/models/InventoryFilter";
import { ProductFilter } from "@domain/product/models/ProductFilter";
import { InventoryServiceLevel } from "@domain/inventory/models/InventoryServiceLevel";
import { InventoryStockLevels } from "@domain/inventory/models/InventoryStockLevels";

export class InventoryRepository implements IInventoryRepository {
  constructor(private dataSource: InventoryDataSource) { }


  async getInventoryForWarehouse(warehouseId?: string, filter?: ProductFilter, page?: number, limit?: number): Promise<PaginatedResource<WarehouseInventory[]>> {
    try {
      const inventoryResponse = await this.dataSource.getInventoryForWarehouse(warehouseId, filter, page, limit);
      return {
        status: 'success',
        ...inventoryResponse,
        data: inventoryResponse.data.map(InventoryMapper.warehouseInventoryToDomain)
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch inventory'
        }
      };
    }
  }

  async getInventory(filter?: InventoryFilter, active = true
  ): Promise<PaginatedResource<ProductInventory[]>> {
    try {
      const inventoryResponse = await this.dataSource.getInventory(filter, active);
      return { status: 'success', ...inventoryResponse, data: inventoryResponse.data.map(InventoryMapper.productInventoryToDomain) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch inventory'
        }
      };
    }
  }

  async getInventoryByProductId(productId: string): Promise<Resource<ProductInventory>> {
    try {
      const data = await this.dataSource.getInventoryByProductId(productId);
      return { status: 'success', data: InventoryMapper.productInventoryToDomain(data) };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to fetch inventory' } };
    }
  }

  async getInventoryServiceLevel(filter?: ProductFilter): Promise<Resource<InventoryServiceLevel>> {
    try {
      const data = await this.dataSource.getInventoryServiceLevel(filter);
      return { status: 'success', data: InventoryServiceLevelMapper.toModel(data) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch inventory service level'
        }
      };
    }
  }

  async getInventoryStockLevels(filter?: ProductFilter): Promise<Resource<InventoryStockLevels>> {
    try {
      const data = await this.dataSource.getInventoryStockLevels(filter);
      return { status: 'success', data: InventoryStockLevelsMapper.toModel(data) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch inventory stock levels'
        }
      };
    }
  }


  async submitManualMutation(mutations: Omit<InventoryMutation, 'id' | 'createdAt'>[], date: Date): Promise<Resource<void>> {
    try {
      await this.dataSource.submitManualMutation(mutations, date);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to submit inventory mutation'
        }
      };
    }
  }

  async submitFileMutation(file: File, warehouseConnectionId: string): Promise<Resource<void>> {
    try {
      await this.dataSource.submitFileMutation(file, warehouseConnectionId);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to submit inventory mutation'
        }
      };
    }
  }

  async getInventoryMutations(warehouseId: string, page: number = 1, limit: number = 20, search?: string): Promise<Resource<{ data: InventoryMutation[]; total: number }>> {
    try {
      const { data, total } = await this.dataSource.getInventoryMutations(warehouseId, page, limit, search);
      return { status: 'success', data: { data, total } };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch inventory mutations'
        }
      };
    }
  }

  async deleteInventoryMutations(mutationIds: string[]): Promise<Resource<void>> {
    try {
      await this.dataSource.deleteInventoryMutations(mutationIds);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to delete inventory mutations'
        }
      };
    }
  }

}